import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { errorCode } from '../lib/errors';
import { LoadFailed } from '../components/QueryState';
import { Link, createFileRoute, redirect, useNavigate, useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatAmount,
  formatNzDate,
  formatRef,
  inspectionFee,
  inspectionFeeIssues,
  nzDate,
  parseAmount,
  parseRef,
} from '@promanage/core';
import {
  INSPECTION_ACTIONS,
  INSPECTION_ACTION_LABELS,
  INSPECTION_CONDITIONS,
  INSPECTION_CONDITION_LABELS,
  INSPECTION_CONDITION_MEANS,
  INSPECTION_KIND_LABELS,
  INSPECTION_STATE_LABELS,
  finaliseIssues,
  inspectionProgress,
  type InspectionAction,
  type InspectionCondition,
} from '@promanage/core/inspection';
import {
  Amount,
  Button,
  Chip,
  Field,
  Input,
  Panel,
  PanelHeader,
  Ref,
  Textarea,
  Select,
} from '@promanage/ui';
import { RecordIdentity } from '../components/RecordIdentity';
import { RecordNotFound } from '../components/RecordNotFound';
import { RecordSkeleton } from '../components/RecordSkeleton';
import { TopBar } from '../components/TopBar';
import { Icon } from '../components/Icon';
import { preparePhoto, runQueue, type QueuedPhoto } from '../features/inspections/photos';
import type {
  InspectionAreaView,
  InspectionDetail,
  InspectionFindingView,
  InspectionPhotoView,
} from '../features/inspections/types';
import { trpc } from '../lib/trpc';
import { useMediaQuery } from '../lib/mediaQuery';
import { useOnline } from '../lib/online';
import { dropCopy, keepCopy, readCopy, type CopyOwner } from '../lib/offlineCopies';
import { isConnectionFailure } from '../lib/errorNotices';
import { useOrgSettings } from '../features/settings/org';
import { ExportPdfButton } from '../features/reports/ExportPdfButton';
import { PhotographUnavailable, exportInspectionReport } from '../features/inspections/reportPdf';
import { DraftStrip } from '../features/inspections/DraftStrip';
import { draftKey } from '../features/inspections/drafts';
import {
  useInspectionDrafts,
  type InspectionDrafts,
} from '../features/inspections/useInspectionDrafts';
import { useSession } from '../features/auth/session';

/**
 * One inspection, addressed by its display ref: /properties/inspections/IS000003.
 *
 * ── THIS IS THE FIELD SCREEN, AND IT IS THE ONLY ONE ─────────────────────────────────────────
 *
 * There is no separate app. The same route an operator reads at a desk is the one an inspector
 * fills in standing in a hallway — which is what makes a bug here a deploy rather than a week in
 * an app store review queue, and what makes a photograph the office can see the same object the
 * phone uploaded. `<input type="file" accept="image/*" capture="environment">` opens the camera on
 * both platforms with no native code at all; everything hard about a field app is the two things
 * below, and neither of them is the camera.
 *
 * ── ONE: THE PREVIOUS VISIT IS SHOWN, NEVER PRE-FILLED ───────────────────────────────────────
 *
 * Every line carries what the last inspection said, and this visit's answer starts blank. Copying
 * the words across is one tap, per line, done on purpose. A report that arrived pre-agreeing with
 * last quarter's is a report an inspector can tap through in the driveway, and it would then be
 * produced at a hearing as a contemporaneous record of a visit that, in the part nobody
 * re-checked, did not happen.
 *
 * ── TWO: THE REPORT CANNOT BE SIGNED OFF WHILE PHOTOGRAPHS ARE IN THE AIR ────────────────────
 *
 * A photograph is claimed on the server the moment the shutter goes, before the bytes exist, so
 * one that never uploads is not indistinguishable from one nobody took. The count is on this page
 * and the sign-off refuses on it — which turns an entire class of sync failure into a sentence
 * read in a driveway rather than a gap discovered at a hearing.
 */
export const Route = createFileRoute('/properties/inspections/$ref')({
  beforeLoad: ({ params }) => {
    const parsed = parseRef(params.ref);
    if (parsed?.entity === 'inspection') {
      const canonical = formatRef('inspection', parsed.sequence);
      if (canonical !== params.ref) {
        throw redirect({
          to: '/properties/inspections/$ref',
          params: { ref: canonical },
          replace: true,
        });
      }
    }
  },
  /*
   * `?room=` is the room a PHONE has open — see `RoomGrid`. Omitted rather than undefined when
   * absent, so every existing `<Link>` here stays valid without one. An id that is not a room on
   * this report is simply no room: the grid shows, rather than an error about a stale link.
   */
  validateSearch: (search: Record<string, unknown>): { room?: string } =>
    typeof search.room === 'string' ? { room: search.room } : {},
  component: InspectionDetailPage,
});

function InspectionDetailPage() {
  const { ref } = Route.useParams();
  const parsed = parseRef(ref);

  const record = useQuery(
    trpc.inspections.byRef.queryOptions(
      { refSeq: parsed?.sequence ?? 0 },
      {
        meta: { errorTitle: 'Could not load that inspection' },
        enabled: parsed?.entity === 'inspection',
        staleTime: 5_000,
      },
    ),
  );

  /*
   * ── THE COPY ON THIS PHONE ───────────────────────────────────────────────────────────────────
   *
   * Written each time the report loads while it is still being worked, dropped once it is not —
   * see lib/offlineCopies.ts for the rules, and docs/SECURITY.md for why they are those rules.
   * Read back only when the server cannot be reached: PAUSED is React Query's word for "offline,
   * waiting", and a connection failure is a phone that reports signal and has none.
   */
  const session = useSession();
  const online = useOnline();
  const owner: CopyOwner | null =
    session === undefined ? null : { userId: session.user.id, orgId: session.orgId };
  const seq = parsed?.entity === 'inspection' ? parsed.sequence : null;
  const unreachable =
    record.data === undefined &&
    (record.fetchStatus === 'paused' ||
      (!online && record.isPending) ||
      (record.error !== null && isConnectionFailure(record.error)));

  const copy = useQuery({
    queryKey: ['offline-copy', 'inspection', owner?.orgId, owner?.userId, seq],
    queryFn: () => (owner === null || seq === null ? null : readCopy(owner, seq)),
    enabled: unreachable && owner !== null && seq !== null,
    // The device answers whether or not there is a network — that is the point of it.
    networkMode: 'always',
    staleTime: Infinity,
  });

  const loaded = record.data;
  useEffect(() => {
    if (loaded === undefined || owner === null || seq === null) return;
    if (loaded.state === 'scheduled' || loaded.state === 'in_progress') {
      void keepCopy(
        owner,
        {
          ref: loaded.ref,
          refSeq: seq,
          title: loaded.propertyAddress,
          subtitle: `${INSPECTION_KIND_LABELS[loaded.kind]} inspection · ${INSPECTION_STATE_LABELS[loaded.state]}`,
        },
        loaded,
      );
    } else {
      // Signed off (or otherwise finished): nothing left to do on site, so nothing kept for it.
      void dropCopy(owner, seq);
    }
    // `owner` is rebuilt every render; what it identifies is the two ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, owner?.userId, owner?.orgId, seq]);

  if (parsed === null || parsed.entity !== 'inspection') {
    return (
      <NotFound
        title={parsed === null ? `“${ref}” is not a record reference` : 'That is not an inspection'}
      >
        {parsed === null
          ? 'An inspection reference looks like IS000003.'
          : `${ref} belongs to another kind of record. Inspection references start with IS.`}
      </NotFound>
    );
  }

  if (unreachable) {
    if (copy.isPending) {
      return (
        <div className="mx-auto max-w-[var(--content-max)] pb-4">
          <TopBar title="Inspection" subtitle="Looking on this phone…" />
          <RecordSkeleton />
        </div>
      );
    }
    const kept =
      copy.data === null || copy.data === undefined ? null : asInspection(copy.data.value);
    if (kept === null || copy.data === null || copy.data === undefined) {
      return (
        <NotFound title="No connection">
          {`${ref} is not saved on this phone. A report is kept here once it has been opened with a connection, while it is booked or in progress.`}
        </NotFound>
      );
    }
    return <Loaded inspection={kept} offlineSince={copy.data.savedAt} />;
  }

  if (record.isPending) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Inspection" subtitle="Loading…" />
        <RecordSkeleton />
      </div>
    );
  }

  /*
   * A MISSING RECORD AND A FAILED LOAD ARE DIFFERENT ANSWERS. Not found is this page's own
   * answer, and the notices stay out of it. Anything else — the server erroring, the connection
   * dropping — is a load that failed, which the notices have already reported, and saying "No
   * such …" about it would tell the operator a record does not exist when it does.
   */
  if (record.error && errorCode(record.error) !== 'NOT_FOUND') {
    return <LoadFailed retry={() => void record.refetch()} retrying={record.isFetching} />;
  }

  if (record.error || !record.data) {
    return (
      <NotFound title="No such inspection">{`${ref} is not an inspection in this agency.`}</NotFound>
    );
  }

  return <Loaded inspection={record.data} />;
}

/**
 * A copy read back off the device, checked for the fields the page cannot render without. The
 * copy's shape version (lib/offlineCopies.ts) already refuses another build's rows; this is the
 * parse at the edge CLAUDE.md asks for, kept to what would otherwise throw mid-render.
 */
/** "at 9:14 am", or "on Thu 2 Oct at 9:14 am" when it was not today. */
function formatSavedAt(iso: string): string {
  const saved = new Date(iso);
  const time = saved.toLocaleTimeString('en-NZ', { hour: 'numeric', minute: '2-digit' });
  if (saved.toDateString() === new Date().toDateString()) return `at ${time}`;
  const day = saved.toLocaleDateString('en-NZ', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  return `on ${day} at ${time}`;
}

function asInspection(value: unknown): InspectionDetail | null {
  if (typeof value !== 'object' || value === null) return null;
  const row = value as Record<string, unknown>;
  return typeof row.id === 'string' &&
    typeof row.ref === 'string' &&
    typeof row.state === 'string' &&
    typeof row.propertyAddress === 'string' &&
    Array.isArray(row.areas)
    ? (value as InspectionDetail)
    : null;
}

function Loaded({
  inspection,
  offlineSince,
}: {
  inspection: InspectionDetail;
  /** Set when this is the copy kept on the phone, read with no connection — when it was saved. */
  offlineSince?: string;
}) {
  const client = useQueryClient();
  const refresh = useCallback(async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: trpc.inspections.byRef.queryKey() }),
      /*
       * SIGNING OFF MOVES THE NEXT DUE DATE — it is measured from the day this one was
       * conducted, so the moment it is signed the property's next visit is thirteen weeks out
       * and it drops off the "due to book" queue. Nothing is written to make that true (the
       * date is derived), but the two screens holding the OLD answer are cached, and a diary
       * still listing a property somebody has just inspected is how it gets inspected twice.
       */
      client.invalidateQueries({ queryKey: trpc.inspections.due.queryKey() }),
      client.invalidateQueries({ queryKey: trpc.inspections.summary.queryKey() }),
    ]);
  }, [client]);

  const start = useMutation(
    trpc.inspections.start.mutationOptions({
      meta: { errorTitle: 'Could not start that inspection' },
      onSuccess: refresh,
    }),
  );

  const editable = inspection.state === 'in_progress';

  /*
   * EVERY EDIT GOES TO THE DEVICE FIRST, then to the server — see `features/inspections/drafts.ts`.
   * A finding recorded in a dead zone survives the tab closing, and is sent when there is signal.
   */
  const session = useSession();
  const drafts = useInspectionDrafts({
    inspectionId: inspection.id,
    userId: session?.user.id ?? null,
    live: editable,
    onSynced: refresh,
  });

  // What each queued edit is about, in the words the report uses.
  const labels = new Map(
    inspection.areas.flatMap((area) => [
      [draftKey(inspection.id, 'area', area.id), `${area.name} notes`] as const,
      ...area.findings.map(
        (finding) =>
          [
            draftKey(inspection.id, 'finding', finding.id),
            `${area.name} · ${finding.name}`,
          ] as const,
      ),
    ]),
  );

  // The progress line counts what the inspector has recorded, sent or not: a condition chosen
  // with no signal is still an answer, and "12 of 40" when they have done 40 is a lie.
  const conditions = inspection.areas.flatMap((area) =>
    area.findings.map((finding) => {
      const draft = drafts.finding(finding.id);
      return draft === undefined ? finding.condition : draft.condition;
    }),
  );

  const progress = inspectionProgress({
    conditions,
    photosPending: inspection.photosPending,
  });

  /*
   * ── ON A PHONE, ONE ROOM AT A TIME ──────────────────────────────────────────────────────────
   *
   * Every room open at once is a page fourteen rooms and a hundred-odd lines long, and the
   * inspector standing in the laundry has to scroll past the bathroom to find it. Below `md` the
   * report is a grid of rooms with where each one is up to; tapping one opens it alone, and the
   * last button in it walks on to the next room — the order the plan walks the house in.
   *
   * IN THE URL, so the phone's own back gesture closes the room rather than leaving the report.
   * Opening a room PUSHES; moving to the next one REPLACES, so back from any room is the grid.
   *
   * EVERY ROOM STAYS MOUNTED, the shut ones hidden. A room's photo strip holds its own upload
   * queue, and unmounting it would drop the retry button of a photograph that failed in a dead
   * zone — the very evidence the sign-off rule exists to protect. Hidden, nothing is lost.
   *
   * A desk keeps the whole report on one page: it is read through there, not filled in.
   */
  const wide = useMediaQuery('(min-width: 48rem)');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const router = useRouter();
  const openArea = wide ? undefined : inspection.areas.find((area) => area.id === search.room);
  const openIndex = openArea === undefined ? -1 : inspection.areas.indexOf(openArea);
  const nextArea = openIndex === -1 ? undefined : inspection.areas[openIndex + 1];
  const pushedRoom = useRef(false);
  const lastRoom = useRef<string | null>(null);

  // A note half-typed saves on blur, so leaving a room blurs it first — the panel is hidden, not
  // unmounted, and a field hidden while focused is not reliably blurred by every browser.
  const leaveField = () => {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  };
  const openRoom = (areaId: string) => {
    leaveField();
    pushedRoom.current = true;
    void navigate({ search: { room: areaId } });
  };
  const walkOn = (areaId: string) => {
    leaveField();
    void navigate({ search: { room: areaId }, replace: true });
  };
  const allRooms = () => {
    leaveField();
    if (pushedRoom.current) {
      pushedRoom.current = false;
      router.history.back();
    } else {
      // Arrived on a room by a link: there is no grid behind it to go back to.
      void navigate({ search: {}, replace: true });
    }
  };

  /*
   * A room opens at its top; the grid comes back with the room just left in view.
   *
   * Keyed on the ID, not the room: every saved line refetches the report and hands back new
   * objects, and keyed on those the page would jump to the top after each tap.
   */
  const openId = openArea?.id ?? null;
  useLayoutEffect(() => {
    if (openId !== null) {
      lastRoom.current = openId;
      document.querySelector('main')?.scrollTo({ top: 0 });
    } else if (lastRoom.current !== null) {
      document
        .querySelector(`[data-room-tile="${lastRoom.current}"]`)
        ?.scrollIntoView({ block: 'center' });
      lastRoom.current = null;
    }
  }, [openId]);

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-24">
      <TopBar
        title={inspection.propertyAddress}
        subtitle={`${INSPECTION_KIND_LABELS[inspection.kind]} inspection · ${inspection.ref}`}
        actions={
          /*
           * ONLY ONCE SIGNED OFF, which the server enforces too. The report is the document an
           * owner files and an adjudicator reads, and until sign-off it can still change.
           */
          inspection.state === 'completed' ? (
            <ExportPdfButton
              label="Download report"
              onExport={() => exportInspectionReport(inspection.refSeq)}
              failureTitle="Could not build the report"
              failureDetail={(cause) =>
                cause instanceof PhotographUnavailable ? cause.message : null
              }
            />
          ) : null
        }
      />

      {offlineSince === undefined ? null : (
        /*
         * SAID, NOT IMPLIED. The inspector is looking at a copy, and has to know what still works:
         * every condition and note does — it waits on the phone as it always has — and what needs
         * the server does not.
         */
        <Panel className="mb-4 flex flex-col gap-1 p-4" role="status">
          <span className="flex flex-wrap items-center gap-2">
            <Chip tone="warn">No connection</Chip>
            <span className="text-[0.8125rem] font-medium">
              The copy saved on this phone {formatSavedAt(offlineSince)}
            </span>
          </span>
          <span className="text-[0.75rem] text-ink-muted">
            Conditions and notes are kept here and send when there is signal. Photographs and
            signing off need a connection.
          </span>
        </Panel>
      )}

      {/*
       * THE SHARED IDENTITY CARD, as on the property and the tenancy this visit belongs to. The
       * facts collapse to one column on a phone, which is where most reports are written — they sit
       * above the rooms rather than beside them, and the rooms are one scroll away.
       */}
      <RecordIdentity
        className="mb-4"
        title={`${INSPECTION_KIND_LABELS[inspection.kind]} inspection`}
        symbol={<Icon name="check" className="size-6" />}
        status={
          <>
            <Ref entity="inspection" sequence={inspection.refSeq} />
            <Chip tone={inspection.state === 'completed' ? 'positive' : 'accent'}>
              {INSPECTION_STATE_LABELS[inspection.state]}
            </Chip>
          </>
        }
        subtitle={inspection.templateName}
        facts={[
          {
            label: 'Property',
            value: (
              <Link
                to="/properties/$ref"
                params={{ ref: inspection.propertyRef }}
                className="hover:text-accent hover:underline"
              >
                {inspection.propertyAddress}
              </Link>
            ),
          },
          { label: 'Booked', value: formatNzDate(inspection.scheduledOn) },
          {
            label: 'Conducted',
            value:
              inspection.conductedOn === null ? (
                <span className="font-normal text-ink-subtle">Not yet</span>
              ) : (
                <>
                  {formatNzDate(inspection.conductedOn)}
                  {inspection.conductedByName === null ? null : (
                    <span className="block text-[0.75rem] font-normal text-ink-subtle">
                      by {inspection.conductedByName}
                    </span>
                  )}
                </>
              ),
          },
          /*
           * WHAT IT COST THE OWNER, and the transaction it can be read through — product
           * principle 4, on the page where somebody asks the question. Absent rather than
           * "charged $0.00" when nothing was charged, which is the great majority of visits.
           */
          ...(inspection.feeTotalCents === null
            ? []
            : [
                {
                  label: 'Charged',
                  value: (
                    <Link
                      to="/financial/transactions"
                      search={
                        inspection.feeTransactionRef === null
                          ? {}
                          : { q: inspection.feeTransactionRef }
                      }
                      className="text-accent hover:underline"
                    >
                      <Amount value={BigInt(inspection.feeTotalCents)} currency tone="neutral" />
                      {inspection.feeTransactionRef === null ? null : (
                        <span className="block text-[0.75rem] font-normal">
                          {inspection.feeTransactionRef}
                        </span>
                      )}
                    </Link>
                  ),
                },
              ]),
        ]}
      />

      {inspection.state === 'scheduled' ? (
        <Panel className="flex flex-col gap-3 p-6">
          <h2 className="text-[1rem] font-bold">This inspection has not been started</h2>
          <p className="max-w-xl text-[0.8125rem] text-ink-muted">
            Starting it builds the report from this property’s inspection plan and brings across
            what the last visit found, so you can see the previous condition beside each line. Do it
            before you leave the office if the property has no signal.
          </p>
          <div>
            {/* Starting builds the report ON THE SERVER, so offline it would only queue a request
                that fires whenever signal returns — later, and somewhere else. */}
            <Button
              disabled={start.isPending || offlineSince !== undefined}
              onClick={() => start.mutate({ inspectionId: inspection.id })}
            >
              {start.isPending ? 'Building the report' : 'Start the inspection'}
            </Button>
            {offlineSince === undefined ? null : (
              <p className="mt-2 text-[0.75rem] text-ink-muted">
                It needs a connection to start. Find a bar of signal, start it, and the rest works
                without one.
              </p>
            )}
          </div>
        </Panel>
      ) : (
        <div className="flex flex-col gap-4">
          {openArea === undefined ? (
            <ProgressBar progress={progress} unsent={drafts.unsent} />
          ) : null}

          <DraftStrip
            drafts={drafts}
            live={editable}
            stateLabel={INSPECTION_STATE_LABELS[inspection.state]}
            labelOf={(key) => labels.get(key) ?? 'A line no longer on this report'}
          />

          {/*
           * NOT UNTIL THE DEVICE HAS BEEN READ. Each row starts from what is saved here if there
           * is anything, and a row that rendered first from the server would show the older answer
           * and then quietly keep it.
           */}
          {drafts.ready ? (
            <>
              {!wide && openArea === undefined ? (
                <RoomGrid areas={inspection.areas} drafts={drafts} onOpen={openRoom} />
              ) : null}

              {openArea === undefined ? null : (
                <div className="flex items-center justify-between gap-2">
                  <Button size="sm" variant="ghost" onClick={allRooms}>
                    <Icon name="chevron-right" size={14} className="rotate-180" />
                    All rooms
                  </Button>
                  <span className="text-[0.75rem] text-ink-subtle tabular-nums">
                    Room {openIndex + 1} of {inspection.areas.length}
                  </span>
                </div>
              )}

              {inspection.areas.map((area) => (
                <div key={area.id} className={wide || area.id === openArea?.id ? '' : 'hidden'}>
                  <AreaPanel
                    inspectionId={inspection.id}
                    area={area}
                    editable={editable}
                    drafts={drafts}
                    onChanged={refresh}
                  />
                </div>
              ))}

              {openArea === undefined ? null : nextArea === undefined ? (
                <Button size="lg" variant="secondary" className="w-full" onClick={allRooms}>
                  {editable ? 'Last room — back to all rooms to sign off' : 'Back to all rooms'}
                </Button>
              ) : (
                <Button size="lg" className="w-full" onClick={() => walkOn(nextArea.id)}>
                  Next: {nextArea.name}
                  <Icon name="chevron-right" size={16} />
                </Button>
              )}
            </>
          ) : (
            <p className="px-1 py-6 text-[0.8125rem] text-ink-muted">
              Reading what is saved on this device…
            </p>
          )}

          {editable && drafts.ready && openArea === undefined ? (
            <SignOff
              inspection={inspection}
              progress={progress}
              drafts={drafts}
              onChanged={refresh}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * The report as a grid of rooms, on a phone — each with where it is up to.
 *
 * Counted the way the progress bar counts the whole report: what this device has recorded, sent or
 * not, so a room finished in a dead zone says Done rather than "0 of 8".
 */
function RoomGrid({
  areas,
  drafts,
  onOpen,
}: {
  areas: readonly InspectionAreaView[];
  drafts: InspectionDrafts;
  onOpen: (areaId: string) => void;
}) {
  return (
    <ul className="grid grid-cols-2 gap-3" aria-label="Rooms">
      {areas.map((area) => {
        const room = inspectionProgress({
          conditions: area.findings.map((finding) => {
            const draft = drafts.finding(finding.id);
            return draft === undefined ? finding.condition : draft.condition;
          }),
          photosPending: 0,
        });

        const status =
          room.total === 0
            ? { text: 'Nothing to check', tone: 'neutral' as const }
            : room.complete
              ? { text: 'Done', tone: 'positive' as const }
              : room.answered === 0
                ? { text: 'Not started', tone: 'neutral' as const }
                : {
                    text: `${String(room.answered)} of ${String(room.total)}`,
                    tone: 'accent' as const,
                  };

        return (
          <li key={area.id}>
            <button
              type="button"
              data-room-tile={area.id}
              onClick={() => onOpen(area.id)}
              className="flex h-full min-h-24 w-full cursor-pointer flex-col justify-between gap-2 rounded-lg bg-surface p-3 text-left shadow-card transition-colors duration-[var(--duration-fast)] hover:bg-surface-sunk"
            >
              <span className="line-clamp-2 text-[0.9375rem] leading-snug font-semibold">
                {area.name}
              </span>
              <span className="flex flex-wrap items-center gap-1.5">
                <Chip tone={status.tone}>{status.text}</Chip>
                {/* The rooms with something wrong are the ones the office will ask about. */}
                {room.needingAttention > 0 ? (
                  <Chip tone="warn">{room.needingAttention} need attention</Chip>
                ) : null}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function ProgressBar({
  progress,
  unsent,
}: {
  progress: ReturnType<typeof inspectionProgress>;
  /** Edits saved on this device and not on the server yet. They block the sign-off too. */
  unsent: number;
}) {
  return (
    <Panel className="flex flex-wrap items-center gap-4 p-4">
      <span className="text-[0.875rem] font-medium tabular-nums">
        {progress.answered} of {progress.total} recorded
      </span>

      {progress.needingAttention > 0 ? (
        <Chip tone="warn">{progress.needingAttention} needing somebody</Chip>
      ) : null}

      {/*
       * THE PHOTOGRAPH COUNT IS THE ONE THAT BLOCKS THE SIGN-OFF, so it is stated in the words the
       * refusal will use rather than as a spinner somebody has to interpret.
       */}
      {progress.photosPending > 0 ? (
        <Chip tone="danger">
          {progress.photosPending} photograph{progress.photosPending === 1 ? '' : 's'} still
          uploading
        </Chip>
      ) : (
        <Chip tone="positive">All photographs uploaded</Chip>
      )}

      {/* The other count that blocks the sign-off, beside the first. `DraftStrip` says why. */}
      {unsent > 0 ? (
        <Chip tone="warn">
          {unsent} edit{unsent === 1 ? '' : 's'} not sent yet
        </Chip>
      ) : null}
    </Panel>
  );
}

function AreaPanel({
  inspectionId,
  area,
  editable,
  drafts,
  onChanged,
}: {
  inspectionId: string;
  area: InspectionAreaView;
  editable: boolean;
  drafts: InspectionDrafts;
  onChanged: () => Promise<unknown>;
}) {
  // What this device last saved for the room, when that has not reached the server yet.
  const saved = drafts.area(area.id);
  const [note, setNote] = useState<string>(
    saved === undefined ? (area.comment ?? '') : (saved.comment ?? ''),
  );

  return (
    <Panel className="p-0">
      <PanelHeader
        title={area.name}
        subtitle={area.guidance ?? undefined}
        className="px-5 pt-5 pb-2"
        aside={area.addedOnTheDay ? <Chip tone="accent">Added on the day</Chip> : null}
      />

      <ul className="divide-y divide-rule border-t border-rule">
        {area.findings.map((finding) => (
          <FindingRow
            key={finding.id}
            inspectionId={inspectionId}
            areaId={area.id}
            finding={finding}
            editable={editable}
            drafts={drafts}
            onChanged={onChanged}
          />
        ))}
      </ul>

      <div className="border-t border-rule px-5 py-3">
        <Field label={`Notes on the ${area.name.toLowerCase()}`}>
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              value={note}
              disabled={!editable}
              onChange={(event) => setNote(event.target.value)}
              onBlur={() => {
                const next = note.trim() === '' ? null : note.trim();
                // Against the latest this device knows of, sent or not.
                const known = saved === undefined ? area.comment : saved.comment;
                if (next === (known ?? null)) return;
                drafts.saveArea(area.id, next);
              }}
            />
          )}
        </Field>

        {saved === undefined ? null : (
          <p className="mt-1 text-[0.75rem] text-ink-subtle">
            {drafts.refusal(saved.key) ?? 'Saved on this device — not sent yet'}
          </p>
        )}

        <PhotoStrip
          inspectionId={inspectionId}
          areaId={area.id}
          findingId={null}
          photos={area.photos}
          editable={editable}
          onChanged={onChanged}
          label="Photograph the room"
        />
      </div>
    </Panel>
  );
}

function FindingRow({
  inspectionId,
  areaId,
  finding,
  editable,
  drafts,
  onChanged,
}: {
  inspectionId: string;
  areaId: string;
  finding: InspectionFindingView;
  editable: boolean;
  drafts: InspectionDrafts;
  onChanged: () => Promise<unknown>;
}) {
  /*
   * WHAT THIS DEVICE LAST SAVED FOR THE LINE wins over what the server has, and it is newer by
   * construction: a draft only exists until the server has accepted it.
   */
  const saved = drafts.finding(finding.id);
  const [condition, setCondition] = useState<InspectionCondition | null>(
    saved === undefined ? finding.condition : saved.condition,
  );
  const [comment, setComment] = useState<string>(
    saved === undefined ? (finding.comment ?? '') : saved.comment,
  );
  const [action, setAction] = useState<InspectionAction>(saved?.action ?? finding.action);
  const knownComment = saved === undefined ? (finding.comment ?? '') : saved.comment;

  // To the device, then to the server when there is signal — see `features/inspections/drafts.ts`.
  const save = (next: {
    condition?: InspectionCondition | null;
    comment?: string;
    action?: InspectionAction;
  }) => {
    drafts.saveFinding(finding.id, {
      condition: next.condition === undefined ? condition : next.condition,
      comment: (next.comment ?? comment).trim(),
      action: next.action ?? action,
    });
  };

  const needsNote = condition === 'damaged' || condition === 'poor';

  return (
    <li className="px-5 py-4">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-[0.875rem] font-medium text-ink">{finding.name}</span>
        {finding.guidance === null ? null : (
          <span className="text-[0.75rem] text-ink-subtle">{finding.guidance}</span>
        )}
        {finding.addedOnTheDay ? <Chip tone="accent">Added on the day</Chip> : null}
      </div>

      {/*
       * ── THE "PREVIOUSLY" LINE ────────────────────────────────────────────────────────────
       *
       * The whole point of the template system, and the reason it is a line of its own rather than
       * a placeholder in the field: an inspector must be able to see what was recorded last time
       * WITHOUT it being what this visit says. Copying it across is a tap, and the tap is the
       * record of a decision somebody made.
       */}
      {finding.previousCondition === null ? null : (
        <div className="mt-1.5 flex flex-wrap items-center gap-2 rounded-md bg-surface-sunk px-3 py-2">
          <span className="text-[0.75rem] font-medium text-ink-subtle">Last time</span>
          <Chip tone="neutral">{INSPECTION_CONDITION_LABELS[finding.previousCondition]}</Chip>
          {finding.previousComment === null ? null : (
            <span className="flex-1 text-[0.8125rem] text-ink-muted">
              “{finding.previousComment}”
            </span>
          )}
          {editable ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setCondition(finding.previousCondition);
                setComment(finding.previousComment ?? '');
                save({
                  condition: finding.previousCondition,
                  comment: finding.previousComment ?? '',
                });
              }}
            >
              Same as last time
            </Button>
          ) : null}
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {INSPECTION_CONDITIONS.map((value) => (
          <button
            key={value}
            type="button"
            disabled={!editable}
            title={INSPECTION_CONDITION_MEANS[value]}
            aria-pressed={condition === value}
            onClick={() => {
              const next = condition === value ? null : value;
              setCondition(next);
              // SAVED EVEN WITHOUT A NOTE. "Damaged" with a blank note is refused by the server, so
              // the queue keeps it on this device and sends it once the note is written — the
              // inspector's choice survives a closed tab either way.
              save({ condition: next });
            }}
            className={[
              'rounded-pill px-3 py-1.5 text-[0.8125rem] font-medium transition-colors',
              condition === value
                ? value === 'damaged'
                  ? 'bg-danger text-white'
                  : value === 'poor'
                    ? 'bg-warn text-white'
                    : 'bg-accent text-accent-ink'
                : 'bg-surface-sunk text-ink-muted hover:text-ink',
            ].join(' ')}
          >
            {INSPECTION_CONDITION_LABELS[value]}
          </button>
        ))}
      </div>

      {/*
       * What the chosen condition MEANS, for a phone — where this page is mostly used, and where
       * the buttons' `title` cannot be hovered. "Poor" against "damaged" is the difference between
       * wear and a claim on the bond, so an inspector has to be able to read it, not remember it.
       */}
      {condition ? (
        <p className="mt-1.5 hidden text-[0.75rem] text-ink-muted pointer-coarse:block">
          {INSPECTION_CONDITION_MEANS[condition]}
        </p>
      ) : null}

      <div className="mt-2 flex flex-wrap gap-3">
        <Field
          label={needsNote ? 'What is wrong (required)' : 'Note'}
          className="min-w-[18rem] flex-1"
        >
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              value={comment}
              disabled={!editable}
              onChange={(event) => setComment(event.target.value)}
              onBlur={() => {
                // Against the latest this device knows of, not only the server's: typing a note
                // back to what the server holds must still replace an unsent draft.
                if (comment === knownComment) return;
                save({ comment });
              }}
            />
          )}
        </Field>

        <Field label="Next" className="min-w-[12rem]">
          {(props) => (
            <Select
              {...props}
              value={action}
              disabled={!editable}
              onChange={(event) => {
                const next = event.target.value as InspectionAction;
                setAction(next);
                save({ action: next });
              }}
              className="h-10 w-full rounded-md bg-surface px-3 text-[0.875rem] ring-1 ring-rule"
            >
              {INSPECTION_ACTIONS.map((value) => (
                <option key={value} value={value}>
                  {INSPECTION_ACTION_LABELS[value]}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      {needsNote && comment.trim() === '' ? (
        <p role="alert" className="mt-1 text-[0.8125rem] font-medium text-danger">
          “{INSPECTION_CONDITION_LABELS[condition]}” needs a note saying what is wrong — it is the
          finding a bond claim or an owner’s repair is argued from. It is kept on this device and
          sent once it has one.
        </p>
      ) : null}

      {/*
       * WHERE THIS LINE HAS GOT TO, when it is not on the server yet: waiting quietly, or refused
       * with the server's own sentence. Nothing at all once it has been accepted.
       */}
      {saved === undefined ? null : drafts.refusal(saved.key) === undefined ? (
        needsNote && comment.trim() === '' ? null : (
          <p className="mt-1 text-[0.75rem] text-ink-subtle">Saved on this device — not sent yet</p>
        )
      ) : (
        // Announced once, as a notice. This is the line remembering why until it is edited.
        <p className="mt-1 flex items-start gap-1.5 text-[0.75rem] text-ink-muted">
          <Chip tone="warn" className="shrink-0">
            Not accepted
          </Chip>
          <span>{drafts.refusal(saved.key)}</span>
        </p>
      )}

      <PhotoStrip
        inspectionId={inspectionId}
        areaId={areaId}
        findingId={finding.id}
        photos={finding.photos}
        editable={editable}
        onChanged={onChanged}
        label="Add a photograph"
      />
    </li>
  );
}

/**
 * Photographs against one line, or one room.
 *
 * ── THE THREE STATES ARE ALL VISIBLE, AND THAT IS THE FEATURE ────────────────────────────────
 *
 * `pending` is a photograph the inspector took that we do not have yet; `stored` is one in the
 * bucket; `abandoned` is one somebody gave up on, with their reason. Every field app shows the
 * first two as one spinner and hides the third entirely, which is why a report can look complete
 * and be missing its evidence. Here the pending count is on the page, on the sign-off, and in the
 * server's refusal.
 */
function PhotoStrip({
  inspectionId,
  areaId,
  findingId,
  photos,
  editable,
  onChanged,
  label,
}: {
  inspectionId: string;
  areaId: string;
  findingId: string | null;
  photos: readonly InspectionPhotoView[];
  editable: boolean;
  onChanged: () => Promise<unknown>;
  label: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [queue, setQueue] = useState<QueuedPhoto[]>([]);

  const claim = useMutation(
    trpc.inspections.photos.claim.mutationOptions({
      meta: { errorTitle: 'Could not start that photograph upload' },
    }),
  );
  const confirm = useMutation(
    trpc.inspections.photos.confirm.mutationOptions({
      meta: { errorTitle: 'Could not finish that photograph upload' },
    }),
  );
  const abandon = useMutation(
    trpc.inspections.photos.abandon.mutationOptions({
      meta: { errorTitle: 'Could not discard that photograph' },
      onSuccess: onChanged,
    }),
  );

  const patch = (localId: string, next: Partial<QueuedPhoto>) => {
    setQueue((rows) => rows.map((row) => (row.localId === localId ? { ...row, ...next } : row)));
  };

  const onFiles = async (files: FileList | null) => {
    if (files === null || files.length === 0) return;

    const prepared = await Promise.all(
      [...files].map(async (file) => {
        const localId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
        try {
          const photo = await preparePhoto(file);
          return { localId, photo, error: null as string | null };
        } catch (error) {
          return {
            localId,
            photo: null,
            error: error instanceof Error ? error.message : 'Could not read that photograph',
          };
        }
      }),
    );

    setQueue((rows) => [
      ...rows,
      ...prepared.map((row) => ({
        localId: row.localId,
        photoId: null,
        previewUrl: row.photo?.previewUrl ?? '',
        state: row.error === null ? ('uploading' as const) : ('failed' as const),
        error: row.error,
        attempts: 0,
      })),
    ]);

    await runQueue(
      prepared.filter((row) => row.photo !== null),
      async (row) => {
        if (row.photo === null) return;
        try {
          const claimed = await claim.mutateAsync({
            inspectionId,
            areaId,
            ...(findingId === null ? {} : { findingId }),
            contentType: row.photo.contentType,
            byteSize: row.photo.byteSize,
            sha256: row.photo.sha256,
            ...(row.photo.capturedAt === null ? {} : { capturedAt: row.photo.capturedAt }),
          });

          patch(row.localId, { photoId: claimed.photoId });

          const response = await fetch(claimed.uploadUrl, {
            method: 'PUT',
            body: row.photo.blob,
          });
          if (!response.ok) throw new Error(`Upload failed (${String(response.status)})`);

          await confirm.mutateAsync({ photoId: claimed.photoId });
          patch(row.localId, { state: 'stored', error: null });
        } catch (error) {
          patch(row.localId, {
            state: 'failed',
            error: error instanceof Error ? error.message : 'That photograph did not upload',
          });
        }
      },
    );

    await onChanged();
  };

  const stored = photos.filter((photo) => photo.state === 'stored');
  const pending = photos.filter((photo) => photo.state === 'pending');
  const abandoned = photos.filter((photo) => photo.state === 'abandoned');

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      {stored.length > 0 ? (
        <Chip tone="neutral">
          <Icon name="image" /> {stored.length}
        </Chip>
      ) : null}

      {pending.length > 0 ? <Chip tone="danger">{pending.length} uploading</Chip> : null}

      {abandoned.map((photo) => (
        <Chip key={photo.id} tone="warn">
          Photograph lost: {photo.abandonedReason}
        </Chip>
      ))}

      {queue
        .filter((row) => row.state === 'failed')
        .map((row) => (
          <span key={row.localId} className="flex items-center gap-2">
            <Chip tone="danger">{row.error}</Chip>
            {row.photoId === null ? null : (
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  abandon.mutate({
                    photoId: row.photoId as string,
                    reason: row.error ?? 'The upload could not be completed',
                  })
                }
              >
                Give up on it
              </Button>
            )}
          </span>
        ))}

      {editable ? (
        <>
          <input
            ref={input}
            type="file"
            accept="image/*"
            /*
             * `capture="environment"` OPENS THE REAR CAMERA on a phone and is ignored on a desktop,
             * where it falls back to the file picker. One input, both surfaces, and no native code.
             * `multiple` is kept because an inspector photographing a damaged wall takes four.
             */
            capture="environment"
            multiple
            hidden
            onChange={(event) => {
              void onFiles(event.target.files);
              event.target.value = '';
            }}
          />
          <Button size="sm" variant="ghost" onClick={() => input.current?.click()}>
            {label}
          </Button>
        </>
      ) : null}
    </div>
  );
}

/**
 * Signing off — the day it was conducted, a note for the owner, and what to charge for it.
 *
 * ── THE FEE IS PROPOSED AND CONFIRMED, NEVER APPLIED SILENTLY ────────────────────────────────
 *
 * The field arrives filled in with the property's agreed inspection fee, because that is what is
 * about to happen and hiding it behind a checkbox would mean the common case is the one nobody
 * reads. It is editable because the exceptions are ordinary: a re-visit charged at half, a first
 * inspection thrown in, a two-hour exit report charged at more. Clearing it charges nothing.
 *
 * WHAT IT ACTUALLY DOES, said on the screen: the fee comes out of the OWNER'S balance the moment
 * this button is pressed, in the same transaction as the sign-off. There is no undo — a charge
 * made in error is reversed on the transaction, and the report itself freezes the instant it is
 * signed. So the total including GST is shown next to the field rather than left for somebody to
 * work out, and the property's own figure is named so a changed number is visibly a change.
 */
function SignOff({
  inspection,
  progress,
  drafts,
  onChanged,
}: {
  inspection: InspectionDetail;
  progress: ReturnType<typeof inspectionProgress>;
  drafts: InspectionDrafts;
  onChanged: () => Promise<unknown>;
}) {
  const today = nzDate(new Date());
  const [conductedOn, setConductedOn] = useState(today);
  const [summary, setSummary] = useState('');

  const org = useOrgSettings();
  const gst = org.data?.gstRegistered === true;

  // The property's agreed fee, as dollars, as the starting value of the field. Empty when no fee
  // is agreed — which is most properties until somebody records one.
  const agreed =
    inspection.propertyInspectionFeeCents === null
      ? null
      : BigInt(inspection.propertyInspectionFeeCents);
  const [fee, setFee] = useState(agreed === null ? '' : formatAmount(agreed));

  const finalise = useMutation(
    trpc.inspections.finalise.mutationOptions({
      meta: { errorTitle: 'Could not sign that inspection off' },
      onSuccess: onChanged,
    }),
  );

  let feeCents: bigint | null = null;
  let feeUnparseable = false;
  try {
    feeCents = fee.trim() === '' ? null : parseAmount(fee);
  } catch {
    feeUnparseable = true;
  }

  const waiting = drafts.unsent - drafts.refused.length;

  // The same functions the server runs, so the refusals appear beside the button rather than after
  // a round trip. The server's are still the gate — these can be stale by a photograph.
  const issues = [
    ...finaliseIssues({ state: 'in_progress', progress, conductedOn, today }),
    ...(feeUnparseable ? ['That is not an amount'] : inspectionFeeIssues(feeCents)),
    /*
     * THE PHOTOGRAPH RULE, APPLIED TO THE WORDS — and here the client IS the gate, because the
     * server cannot know what is sitting on this phone. Signing off freezes the report by trigger,
     * so an edit still on the device at that moment could never be added afterwards.
     */
    ...(waiting > 0
      ? [
          `${String(waiting)} edit${waiting === 1 ? '' : 's'} made on this device ` +
            `${waiting === 1 ? 'has' : 'have'} not reached the server yet. Reconnect and let ` +
            `${waiting === 1 ? 'it' : 'them'} send — a report signed off without ` +
            `${waiting === 1 ? 'it' : 'them'} is missing what you recorded.`,
        ]
      : []),
    ...(drafts.refused.length > 0
      ? [
          `${String(drafts.refused.length)} edit${drafts.refused.length === 1 ? ' was' : 's were'} ` +
            'refused by the server — change the line or discard the edit first.',
        ]
      : []),
  ];

  const charge = inspectionFee(feeCents ?? 0n, gst);
  const changed = (feeCents ?? null) !== (agreed ?? null);

  return (
    <Panel className="p-0">
      <PanelHeader
        title="Sign off"
        subtitle="Once signed off, nothing on this report can be changed"
        className="px-5 pt-5 pb-2"
      />

      <div className="flex flex-col gap-3 border-t border-rule px-5 py-4">
        {progress.answered < progress.total ? (
          /*
           * SHOWN, NOT REFUSED. `not_inspected` exists for the item nobody could reach, so an
           * inspector who means it has a way to say so; refusing to finish until every line is
           * answered is how "Good" gets ticked on the way out of a house. The number goes in front
           * of them and the decision stays theirs.
           */
          <p className="text-[0.8125rem] text-ink-muted">
            {progress.total - progress.answered} line
            {progress.total - progress.answered === 1 ? ' has' : 's have'} nothing recorded against
            {progress.total - progress.answered === 1 ? ' it' : ' them'}. They will show as blank on
            the report — mark anything you could not get to as “Not inspected”.
          </p>
        ) : null}

        <div className="flex flex-wrap items-end gap-3">
          <Field label="Conducted on" className="w-[12rem]">
            {(props) => (
              <Input
                {...props}
                type="date"
                value={conductedOn}
                max={today}
                onChange={(event) => setConductedOn(event.target.value)}
              />
            )}
          </Field>

          <Field label="Inspection fee" className="w-[10rem]">
            {(props) => (
              <Input
                {...props}
                inputMode="decimal"
                placeholder="0.00"
                value={fee}
                onChange={(event) => setFee(event.target.value)}
                className="tabular-nums"
              />
            )}
          </Field>

          <Field label="Summary for the owner" className="min-w-[20rem] flex-1">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={summary}
                onChange={(event) => setSummary(event.target.value)}
              />
            )}
          </Field>

          <Button
            disabled={issues.length > 0 || finalise.isPending}
            onClick={() =>
              finalise.mutate({
                inspectionId: inspection.id,
                conductedOn,
                feeCents: feeCents === null ? null : feeCents.toString(),
                ...(summary.trim() === '' ? {} : { summary: summary.trim() }),
              })
            }
          >
            {finalise.isPending ? 'Signing off' : 'Sign off'}
          </Button>
        </div>

        {/*
         * WHAT THE BUTTON IS ABOUT TO DO TO SOMEBODY'S MONEY, in one line, in the words the owner
         * statement will use. Product principle 4 read backwards: a figure that will appear on a
         * document should be visible before it is created, not only afterwards.
         */}
        {issues.length === 0 ? (
          <p className="text-[0.8125rem] text-ink-muted">
            {charge.totalCents === 0n ? (
              <>
                Nothing will be charged for this inspection.
                {agreed !== null && agreed > 0n ? (
                  <>
                    {' '}
                    This property’s agreed fee is <Amount value={agreed} currency tone="neutral" />.
                  </>
                ) : null}
              </>
            ) : (
              <>
                <Amount value={charge.totalCents} currency className="font-semibold" />
                {gst ? (
                  <>
                    {' '}
                    (<Amount value={charge.feeCents} currency tone="neutral" /> + GST)
                  </>
                ) : null}{' '}
                will be charged to the owner
                {inspection.propertyAddress ? ` of ${inspection.propertyAddress}` : ''}, dated{' '}
                {formatNzDate(conductedOn)}.
                {changed && agreed !== null ? (
                  <>
                    {' '}
                    This property’s agreed fee is <Amount value={agreed} currency tone="neutral" />.
                  </>
                ) : null}
                {changed && agreed === null ? <> This property has no agreed fee.</> : null}
              </>
            )}
          </p>
        ) : null}

        {issues.length > 0 ? (
          <ul role="alert" className="text-[0.8125rem] font-medium text-danger">
            {issues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </Panel>
  );
}

function NotFound({ title, children }: { title: string; children: ReactNode }) {
  return (
    <RecordNotFound
      record="Inspection"
      title={title}
      back={{ label: 'Back to inspections', link: { to: '/properties/inspections' } }}
    >
      {children}
    </RecordNotFound>
  );
}
